import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { Switch, Position } from '@blueprintjs/core/dist';
import { DateInput, TimePicker } from '@blueprintjs/datetime';
import { difference, values, keyBy, isEmpty, isEqual, uniqWith } from 'lodash';
import * as Select from 'react-select';
import { RegexFilter } from '../../utils/filters';
import { getSnapshot } from 'mobx-state-tree';
import { Creatable } from 'react-select';
import * as moment from 'moment';

export interface FormFieldProps {
  type?: string;
  value?: string;
  label: string;
  placeholder?: string;
  rules?: string;
  name: string;
  fields?: FormFieldProps[];
  default?: string;
}
interface MultiCreateProps {
  // tslint:disable-next-line:no-any
  field: any;
  // tslint:disable-next-line:no-any
  listStore: any;

  multi: boolean | undefined;
}

const MultiCreateComponent = observer((props: MultiCreateProps) => {
  const { multi, listStore, field } = props;
  const allOptions = uniqWith(listStore.list.map(l => getSnapshot(l))
    .concat(
    multi ?
      field.value ? values(field.value) : [] :
      field.value ? field.value : undefined
    // tslint:disable-next-line:no-any
    ), (a, b) => isEqual(a.name, b.name)) as any;
  return (
    <Creatable
      multi={multi}
      options={allOptions}
      labelKey={'name'}
      valueKey={'name'}
      // tslint:disable-next-line:no-any
      value={multi ?
        // tslint:disable-next-line:no-any
        values(field.values()).map(v => (v as any).name) :
        field.value && isEmpty(field.value.name) === false ? field.value.name : undefined
      }
      newOptionCreator={({ label, labelKey, valueKey }) => {
        // tslint:disable-next-line:no-any
        return { name: label, _id: null } as any;
      }}
      onInputChange={e => {
        listStore.addFilter(`multiSelectFilter${field.id}`, e,
          RegexFilter.create({
            id: `${field.id}`,
            caseInsensitive: true,
            field: 'name',
            value: e
          }));
      }}
      onChange={e => {
        if (multi) {
          // tslint:disable-next-line:no-any
          const fieldVals = values(field.value).map(v => (v as any).name);
          // tslint:disable-next-line:no-any
          const valueIdMap = keyBy(e as any[], v => v.name);
          const options = (e as { name: string }[]).map(i => i.name);
          const newValues = difference(options, fieldVals);
          const removedValues = difference(fieldVals, options);
          newValues.map(v => {
            field.add({ name: v, _id: valueIdMap[v]._id }, { key: v });
          });
          removedValues.map(v => {
            field.del(v);
          });
        } else {
          field.set('value', e);
        }
      }}
      clearable={false}
    />);
});

// TODO IMPROVE

export const ServiceBackedSelect = inject('store')(observer(
  (props: {
    creatable: boolean | undefined,
    multi: boolean | undefined,
    // tslint:disable-next-line:no-any
    field: any
    // tslint:disable-next-line:no-any
  } & { store?: any }) => {
    const listStore = props.store.storeFactory.createListStore(props.field.extra.service); // .listModel();
    listStore.refresh();
    return (
      props.creatable ?
        <MultiCreateComponent field={props.field} multi={props.multi} listStore={listStore} /> :
        <SelectWithService field={props.field} multi={props.multi} listStore={listStore} />);
  }));

export interface FieldInputProps {
  // tslint:disable-next-line:no-any
  field: any;

  showLabels: boolean;

  large?: boolean;
}

// tslint:disable-next-line:no-any
const SelectWithService = observer((props: { multi: boolean | undefined, field: any, listStore: any }) => (
  <Select
    name={props.field.name}
    multi={props.multi}
    // tslint:disable-next-line:no-any
    options={uniqWith(props.listStore.list.map(item => getSnapshot(item) as any), (a, b) => isEqual(a.name, b.name))}
    scrollMenuIntoView={true}
    labelKey={'name'}
    valueKey={'name'}
    // tslint:disable-next-line:no-any
    value={props.multi ? values(props.field.value).map(f => (f as any).name) : props.field.value.name}
    onInputChange={e => {
      if (e.length > 0) {
        props.listStore.addFilter(`selectFilter${props.field.id}`, e,
          RegexFilter.create({
            id: `${props.field.id}`,
            caseInsensitive: true,
            field: 'name',
            value: e
          }));
      }
    }}
    onChange={e => {
      if (props.multi) {
        // tslint:disable-next-line:no-any
        const fieldVals = values(props.field.value).map(v => (v as any).name);
        // tslint:disable-next-line:no-any
        const valueIdMap = keyBy(e as any[], v => v.name);
        const options = (e as { name: string }[]).map(i => i.name);
        const newValues = difference(options, fieldVals);
        const removedValues = difference(fieldVals, options);
        newValues.map(v => {
          props.field.add({ name: v, _id: valueIdMap[v]._id }, { key: v });
        });
        removedValues.map(v => {
          props.field.del(v);
        });
      } else {
        if (e) {
          props.field.set('value', e);
          // tslint:disable-next-line:no-console
          console.log(`
          gotten option as ${JSON.stringify(e)}
          setting field value to ${JSON.stringify(props.field.value)}`);
        } else {
          props.field.set('value', null);
        }
      }
      // props.field.sync(e);
    }}
    clearable={false}
  />
));

const FieldInput = observer((props: FieldInputProps) => {
  const classNames = `pt-fill pt-input ${props.field.error ? ' pt-intent-danger' : ''}`;
  const extra = props.field.extra;
  if (props.field.type === 'textarea') {
    return (<textarea className={classNames} {...props.field.bind() } dir="auto" />);
  } else if (props.field.type === 'select') {
    // extra: { creatable: true/false, multi: true/false service/option }
    if (!extra) {
      throw new Error('Invalid field defination for "select" type, requires extra to be set');
    } else if (!extra.options && !extra.service) {
      throw new Error(
        `Invalid field defination for "select" type, 
        requires options or service in extra gotten: [${JSON.stringify(extra)}]`);
    }
    const creatable = extra.creatable as boolean | undefined;
    const multi = extra.multi as boolean | undefined;
    if (extra.options) {
      if (creatable) {
        throw new Error(`cannot set creatable with static options, user service instead`);
      }
      return (
        <Select
          multi={multi}
          simpleValue
          menuContainerStyle={{ 'zIndex': 999 }}
          options={extra.options}
          {...props.field.bind() }
          clearable={false}
        />
      );
    } else {
      return (
        <ServiceBackedSelect
          creatable={creatable}
          multi={multi}
          field={props.field}
        />);
    }
  } else if (props.field.type === 'boolean') {
    return (
      <Switch {...props.field.bind() } onChange={e => props.field.value = (e.target as HTMLInputElement).checked} />
    );
  } else if (props.field.type === 'date') {
    return (
      <DateInput
        inputProps={{ ...props.field.bind() }}
        popoverPosition={Position.RIGHT}
        popoverProps={{ inline: false }}
        value={props.field.value}
        onChange={e => props.field.value = e}
        minDate={moment().subtract(100, 'y').toDate()}
      />
    );
  } else if (props.field.type === 'autosuggest') {
    throw new Error(`deprecated type for ${props.field.name}`);
  } else if (props.field.type === 'creatable_select') {
    throw new Error(`deprecated type for ${props.field.name}`);
  } else if (props.field.type === 'number') {
    return (
      <input
        dir="auto"
        className={classNames}
        {...props.field.bind() }
      />
    );
  } else if (props.field.type === 'time') {
    return (
      <TimePicker
        value={props.field.value ? props.field.value : undefined}
        onChange={e => {
          props.field.set('value', e);
        }}
      />);
  }
  return (
    <input
      dir="auto"
      className={classNames}
      {...props.field.bind() }
    />
  );
});

export const FieldLabel = observer((props: {
  id: string;
  label: string | JSX.Element;
  field: JSX.Element;
  error?: string;
  renderClassName?: string;
}) => (
    <div className={`pt-form-group ${props.error ? ' pt-intent-danger' : ''}`}>
      <label className={`pt-label b ${props.renderClassName}`} htmlFor={props.id}>
        {props.label}
      </label>
      <div className="pt-form-content">
        {props.field}
        {props.error ?
          <div className="pt-form-helper-text pt-intent-danger">{props.error}</div> : null}
      </div>
    </div>
  ));

export const FormField = observer((props: FieldInputProps) => {
  if (props.showLabels) {
    return (
      <div className={`pt-form-group ${props.field.error ? ' pt-intent-danger' : ''}`}>
        <label className="pt-label b" htmlFor={props.field.id}>
          {props.field.label}
        </label>
        <div className="pt-form-content">
          <FieldInput field={props.field} showLabels={props.showLabels} />
          {props.field.error ?
            <div className="pt-form-helper-text pt-intent-danger">{props.field.error}</div> : null}
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <div
          className={
            ['pt-input-group ',
              props.large ? 'pt-large' : '',
              props.field.error ? 'pt-intent-danger' : ''
            ].join(' ')}
        >
          {props.field.extra && props.field.extra.iconName ?
            <span
              className={['pt-icon', props.field.extra ? `pt-icon-${props.field.extra.iconName}` : ''].join(' ')}
            /> : undefined}
          <FieldInput field={props.field} showLabels={props.showLabels} />
        </div>
        {props.field.error ?
          <div className="pv1 db pt-intent-danger pt-tag">
            {props.field.error}
          </div> : undefined
        }
      </div>

    );
  }
});


// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/input/index.tsx